<template>
  <div class="survey-page">
    <div class="survey-container">
      <!-- Loading -->
      <div v-if="loading" class="survey-loading">
        <div class="spinner"></div>
      </div>

      <!-- Question Card -->
      <div v-else-if="showQuestion" class="survey-card">
        <div class="survey-header">
          <img src="/logo1.png" alt="Logo" class="logo" />
        </div>
        <div class="survey-body">
          <div class="question-box">
            <p class="survey-question">{{ questionText }}</p>
            <div class="survey-answers">
              <label
                v-for="ans in answers"
                :key="ans.ansId"
                class="radio-label"
              >
                <input
                  type="radio"
                  name="surveyAnswer"
                  :value="ans.ansId"
                  v-model="selectedAnsId"
                  class="radio-input"
                />
                <span class="radio-circle"></span>
                <span class="radio-text">{{ ans.ansAnswer }}</span>
              </label>
            </div>
          </div>
          <button
            class="submit-btn"
            :class="{ active: selectedAnsId !== null }"
            :disabled="selectedAnsId === null || submitting"
            @click="handleSubmit"
          >
            Submit
          </button>
        </div>
      </div>

      <!-- Response -->
      <div v-else-if="showResponse" class="survey-card">
        <div class="survey-header">
          <img src="/logo1.png" alt="Logo" class="logo" />
        </div>
        <div class="survey-body">
          <p class="response-message">{{ responseMessage }}</p>
        </div>
      </div>

      <!-- Error -->
      <div v-else-if="showError" class="survey-card">
        <div class="survey-header">
          <img src="/logo1.png" alt="Logo" class="logo" />
        </div>
        <div class="survey-body">
          <p class="error-message">{{ errorMessage }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, getCurrentInstance } from "vue";
import { useRoute } from "vue-router";

const route = useRoute();
const { proxy }: any = getCurrentInstance();

const loading = ref(true);
const showQuestion = ref(false);
const showResponse = ref(false);
const showError = ref(false);

const questionText = ref("");
const answers = ref<any[]>([]);
const selectedAnsId = ref<number | null>(null);
const submitting = ref(false);
const responseMessage = ref("");
const errorMessage = ref("");

const ledKey = (route.query.ledKey as string) || "";
const queId = (route.query.question as string) || "";
const language = (route.query.language as string) || "English";
const answerFromUrl = (route.query.answer as string) || "";

function displayError(msg: string) {
  errorMessage.value = msg;
  loading.value = false;
  showQuestion.value = false;
  showResponse.value = false;
  showError.value = true;
}

function displayResponse(msg: string) {
  responseMessage.value = msg;
  loading.value = false;
  showQuestion.value = false;
  showError.value = false;
  showResponse.value = true;
}

function submitAnswer(ansId: number | string) {
  loading.value = true;
  showQuestion.value = false;

  proxy.$http
    .surveyAnswer({
      ledKey,
      queId,
      ansId,
      language,
    })
    .then((res: any) => {
      const data = res.code === 200 ? res.data : res;
      let text = "";
      if (Array.isArray(data) && data.length > 0) {
        text = data[0].response || data[0].Response || data[0].message || "";
      } else if (typeof data === "string") {
        text = data;
      }
      displayResponse(text || "Thank you for your response.");
    })
    .catch(() => {
      displayError("Failed to submit your answer. Please try again.");
    });
}

function loadQuestion() {
  proxy.$http
    .surveyQuestion({ ledKey, language, queId })
    .then((res: any) => {
      const data = res.code === 200 ? res.data : res;
      if (!data || !Array.isArray(data) || data.length === 0) {
        displayError("No question found.");
        return;
      }

      const totalques = data[0].totalques || "";
      const qText = data[0].question || "";
      questionText.value = (totalques ? totalques + ". " : "") + qText;

      answers.value = data
        .filter((row: any) => row.ansAnswer !== undefined)
        .sort((a: any, b: any) => (a.ansOrd || 0) - (b.ansOrd || 0));

      loading.value = false;
      showQuestion.value = true;
    })
    .catch(() => {
      displayError("Failed to load the question. Please try again.");
    });
}

function handleSubmit() {
  if (selectedAnsId.value === null) return;
  submitting.value = true;
  submitAnswer(selectedAnsId.value);
}

onMounted(() => {
  if (!ledKey || !queId) {
    displayError("Invalid survey link. Missing required parameters.");
    return;
  }

  if (answerFromUrl) {
    submitAnswer(answerFromUrl);
  } else {
    loadQuestion();
  }
});
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&display=swap");

.survey-page {
  font-family: "Montserrat", sans-serif;
  background: linear-gradient(to bottom, #456a33, #9ab477);
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0;
  padding: 0;
}

.survey-container {
  width: 100%;
  max-width: 640px;
  padding: 20px;
}

.survey-card {
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

.survey-header {
  text-align: center;
  padding: 24px 24px 0;
}

.logo {
  height: 50px;
}

.survey-body {
  padding: 20px 32px 32px;
}

.question-box {
  border: 2px solid #6e8e54;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
}

.survey-question {
  color: #333;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.6;
  margin-bottom: 20px;
}

.survey-answers {
  display: flex;
  flex-direction: row;
  gap: 24px;
  flex-wrap: wrap;
}

.radio-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 8px;
}

.radio-input {
  display: none;
}

.radio-circle {
  width: 20px;
  height: 20px;
  border: 2px solid #999;
  border-radius: 50%;
  display: inline-block;
  position: relative;
  flex-shrink: 0;
  transition: border-color 0.2s;
}

.radio-input:checked + .radio-circle {
  border-color: #456a33;
}

.radio-input:checked + .radio-circle::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 10px;
  height: 10px;
  background: #456a33;
  border-radius: 50%;
}

.radio-text {
  font-size: 15px;
  color: #333;
}

.submit-btn {
  width: 100%;
  padding: 12px;
  font-family: "Montserrat", sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  border: none;
  border-radius: 5px;
  cursor: not-allowed;
  opacity: 0.5;
  background: linear-gradient(270deg, #396281, #27a6de, #396281);
  background-size: 600% 600%;
  animation: gradientAnimation 10s ease infinite;
  transition: opacity 0.2s;
}

.submit-btn.active {
  cursor: pointer;
  opacity: 1;
}

@keyframes gradientAnimation {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

.response-message {
  color: #333;
  font-size: 15px;
  line-height: 1.7;
  text-align: center;
}

.error-message {
  color: #c0392b;
  font-size: 16px;
  text-align: center;
  line-height: 1.6;
}

.survey-loading {
  text-align: center;
  padding: 60px 0;
}

.spinner {
  display: inline-block;
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .survey-container { padding: 16px; }
  .survey-body { padding: 16px 20px 24px; }
  .question-box { padding: 16px; }
  .survey-question { font-size: 14px; }
  .radio-text { font-size: 14px; }
  .submit-btn { padding: 10px; font-size: 14px; }
  .logo { height: 40px; }
  .response-message { font-size: 14px; }
}
</style>
